import { FALLBACK_LOCALE, localeToLanguage, normalizeLocale } from "./localeResolution.js";

function readLocalizedMapValue(map, locale, fallbackLocale) {
  if (!map || typeof map !== "object") {
    return null;
  }
  const normalized = normalizeLocale(locale);
  const fallback = normalizeLocale(fallbackLocale);
  const language = localeToLanguage(normalized);
  const fallbackLanguage = localeToLanguage(fallback);

  if (map[normalized] != null && map[normalized] !== "") {
    return map[normalized];
  }
  if (map[language] != null && map[language] !== "") {
    return map[language];
  }
  const languageMatch = Object.keys(map).find(
    (key) => localeToLanguage(key) === language && map[key] != null && map[key] !== ""
  );
  if (languageMatch) {
    return map[languageMatch];
  }
  if (map[fallback] != null && map[fallback] !== "") {
    return map[fallback];
  }
  if (map[fallbackLanguage] != null && map[fallbackLanguage] !== "") {
    return map[fallbackLanguage];
  }
  return null;
}

function hasTranslationValue(value) {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

function readTranslationJsonField(obj, locale, fieldName, fallbackLocale) {
  const translations = obj?.translationsJson;
  if (!translations || typeof translations !== "object") {
    return null;
  }
  const normalizedLocale = normalizeLocale(locale);
  const normalizedFallback = normalizeLocale(fallbackLocale);
  const language = localeToLanguage(normalizedLocale);

  const exact = translations[normalizedLocale]?.[fieldName];
  if (hasTranslationValue(exact)) {
    return exact;
  }
  for (const [loc, fields] of Object.entries(translations)) {
    if (localeToLanguage(loc) === language && hasTranslationValue(fields?.[fieldName])) {
      return fields[fieldName];
    }
  }
  const fallback = translations[normalizedFallback]?.[fieldName];
  if (hasTranslationValue(fallback)) {
    return fallback;
  }
  return null;
}

/**
 * Resolve a localized content field from common manifest/content shapes.
 */
export function resolveLocalizedField(
  obj,
  locale,
  fieldName,
  fallbackLocale = FALLBACK_LOCALE,
  emptyFallback = ""
) {
  if (!obj || typeof obj !== "object") {
    return emptyFallback;
  }

  const normalizedLocale = normalizeLocale(locale);
  const normalizedFallback = normalizeLocale(fallbackLocale);
  const language = localeToLanguage(normalizedLocale);

  const translationJsonValue = readTranslationJsonField(
    obj,
    normalizedLocale,
    fieldName,
    normalizedFallback
  );
  if (translationJsonValue != null && translationJsonValue !== "") {
    return typeof translationJsonValue === "string"
      ? translationJsonValue
      : String(translationJsonValue);
  }

  const i18nExact = obj.i18n?.[normalizedLocale]?.[fieldName];
  if (i18nExact != null && i18nExact !== "") {
    return String(i18nExact);
  }

  for (const [loc, fields] of Object.entries(obj.i18n || {})) {
    if (localeToLanguage(loc) === language && fields?.[fieldName] != null && fields[fieldName] !== "") {
      return String(fields[fieldName]);
    }
  }

  const i18nFallback = obj.i18n?.[normalizedFallback]?.[fieldName];
  if (i18nFallback != null && i18nFallback !== "") {
    return String(i18nFallback);
  }

  const fieldValue = obj[fieldName];
  if (fieldValue != null && typeof fieldValue === "object" && !Array.isArray(fieldValue)) {
    const localized = readLocalizedMapValue(fieldValue, normalizedLocale, normalizedFallback);
    if (localized != null && localized !== "") {
      return String(localized);
    }
  }

  if (typeof fieldValue === "string" && fieldValue !== "") {
    const isFallbackLocale =
      normalizedLocale === normalizedFallback ||
      localeToLanguage(normalizedLocale) === localeToLanguage(normalizedFallback);
    if (isFallbackLocale) {
      return fieldValue;
    }
    return emptyFallback;
  }

  return emptyFallback;
}

function readPageI18nField(content, locale, fieldName, fallbackLocale) {
  if (!content?.i18n || typeof content.i18n !== "object") {
    return null;
  }
  const normalized = normalizeLocale(locale);
  const fallback = normalizeLocale(fallbackLocale);
  const language = localeToLanguage(normalized);

  const exact = content.i18n[normalized]?.[fieldName];
  if (hasTranslationValue(exact)) {
    return exact;
  }

  for (const [loc, fields] of Object.entries(content.i18n)) {
    if (localeToLanguage(loc) === language && hasTranslationValue(fields?.[fieldName])) {
      return fields[fieldName];
    }
  }

  const fallbackExact = content.i18n[fallback]?.[fieldName];
  if (hasTranslationValue(fallbackExact)) {
    return fallbackExact;
  }

  return null;
}

/**
 * Catalog-first page field for AutoMapa consumer surfaces.
 * Uses manifest content.i18n[locale] when present; ignores legacy top-level strings
 * that may be authored in the wrong language.
 */
export function resolveCatalogFirstPageField(
  content,
  locale,
  fieldName,
  catalogFallback,
  fallbackLocale = FALLBACK_LOCALE
) {
  if (!content || typeof content !== "object") {
    return catalogFallback;
  }

  const fromI18n = readPageI18nField(content, locale, fieldName, fallbackLocale);
  if (fromI18n != null && fromI18n !== "") {
    return typeof fromI18n === "string" ? fromI18n : String(fromI18n);
  }

  const fieldValue = content[fieldName];
  if (fieldValue != null && typeof fieldValue === "object" && !Array.isArray(fieldValue)) {
    const localized = readLocalizedMapValue(fieldValue, locale, fallbackLocale);
    if (localized != null && localized !== "") {
      return String(localized);
    }
  }

  return catalogFallback;
}

/**
 * Catalog-first CTA/object field — merges locale-specific manifest override onto catalog defaults.
 */
export function resolveCatalogFirstPageObject(
  content,
  locale,
  fieldName,
  catalogDefaults,
  fallbackLocale = FALLBACK_LOCALE
) {
  const fromI18n = readPageI18nField(content, locale, fieldName, fallbackLocale);
  if (fromI18n && typeof fromI18n === "object" && !Array.isArray(fromI18n)) {
    return { ...catalogDefaults, ...fromI18n };
  }

  const fieldValue = content?.[fieldName];
  if (fieldValue != null && typeof fieldValue === "object" && !Array.isArray(fieldValue)) {
    const localized = readLocalizedMapValue(fieldValue, locale, fallbackLocale);
    if (localized != null && typeof localized === "object" && !Array.isArray(localized)) {
      return { ...catalogDefaults, ...localized };
    }
  }

  return catalogDefaults;
}
